import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as XLSX from "xlsx";
import Button from "../../components/utils/Button";
import Table from "../../components/utils/Table";
import { downloadExcelSampleFile } from "../../helpers/commonFunction";
import purchaseSimActions from "../../redux/inventory/purchaseSim/actions";
import { useDispatch, useSelector } from "react-redux";
import ToastAlert from "../../components/utils/ToastAlert";
import CardLayout from "../../components/utils/CardLayout";

const { purchaseInventory, resetMessage } = purchaseSimActions;

const MAX_RECORDS = 5000;
const BASE_URL = process.env.REACT_APP_SITE_BASE_URL;

const PurchaseSim = () => {
  const dispatch = useDispatch();
  const [copiedField, setCopiedField] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [previewColumn, setPreviewColumn] = useState([]);
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const today = new Date();
  const nextYearDate = new Date(today);
  nextYearDate.setFullYear(today.getFullYear() + 1);
  const formattedNextYear = nextYearDate.toISOString().split("T")[0];
  const [totalUpload, setTotalUpload] = useState(0);
  const [vailidData, setVailidData] = useState(0);
  const [isvailidFlag, setIsvailidFlag] = useState(false);
  const [vailidError, setVailidError] = useState("");
  const {
    purchaseInventoryLoading,
    purchaseInventoryMessage,
    purchaseInventoryError,
    purchaseInventoryFResult
  } = useSelector((state) => state.purchaseSim);


  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      purchaseNo: "",
      purchaseDate: toDate,
      expiryDate: formattedNextYear,
    },
  });

  const watchPurchaseNumber = watch("purchaseNo");

  const generatePurchaseNumber = () => {
    const now = new Date();

    const yy = String(now.getFullYear()).slice(-2); // yy
    const mm = String(now.getMonth() + 1).padStart(2, "0"); // mm
    const dd = String(now.getDate()).padStart(2, "0"); // dd

    const hh = String(now.getHours()).padStart(2, "0"); // hh
    const min = String(now.getMinutes()).padStart(2, "0"); // mm
    const seconds = String(now.getSeconds()).padStart(2, "0"); // ss

    return `PU${yy}${mm}${dd}${hh}${min}${seconds}`;
  };

  useEffect(() => {
    if (purchaseInventoryMessage) {
      ToastAlert({ type: "success", text: purchaseInventoryMessage });
      handleReset();
      setValue("purchaseNo", generatePurchaseNumber());
    }
    if (purchaseInventoryError) {
      ToastAlert({ type: "error", text: purchaseInventoryError });
    }
  }, [purchaseInventoryError, purchaseInventoryMessage]);

  useEffect(() => {
    setValue("purchaseNo", generatePurchaseNumber());
    return () => {
      dispatch(resetMessage());
    };
  }, []);

  const downloadSampleFile = () => {
    const data = [
      {
        SerialNumber: "1234567890123456789",
        LPA: "LPA:1$T-xxxx.xxx.xxxx-aaaa-bbbbb-cccc",
      },
    ];
    downloadExcelSampleFile(data, "purchase-new-sim-sample-file.xlsx");
  };

  const onSubmit = (data) => {
    const simDetails = rowData.map((item) => {
      return {
        simNo: item?.SerialNumber,
        lpa: item?.LPA,
      };
    });

    const payload = {
      ...data,
      vendorId: 101,
      simType: 26,
      simDetails,
    };
    dispatch(purchaseInventory(payload));
  };

  const handleReset = () => {
    setPreviewColumn([]);
    setPreviewData([]);
    setValue("expiryDate", formattedNextYear);
    setTotalUpload(0);
    setVailidData(0);
    setVailidError("");
  };

  const handleCopy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(value);
      setTimeout(() => setCopiedField(""), 500);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const onFileUpload = (e) => {
    const fileReader = new FileReader();
    let file = e.target.files[0];

    setVailidError("");
    setIsvailidFlag(false);
    setPreviewColumn([]);
    setPreviewData([]);
    setTotalUpload(0);
    setVailidData(0);

    if (!file) return;

    // Validate file type
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setVailidError("Please upload a valid file (.xlsx, .xls)");
      return;
    }

    fileReader.readAsArrayBuffer(file);

    fileReader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });

      // Get first sheet
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // Convert to JSON (rows)
      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        defval: "", // avoid undefined
      });

      // // Get columns (headers)
      const headers = Object.keys(jsonData[0] || {});

      const required = ["SerialNumber", "LPA"];
      const isValid =
        headers.length === 2 && required.every((col) => headers.includes(col));

      if (!isValid) {
        setVailidError(
          "Invalid file. Required columns: Serial Number and LPA.",
        );
        return;
      }

      if (jsonData?.length > MAX_RECORDS) {
        setVailidError(
          "Maximum 5000 records allowed.",
        );
        return;
      }

      const heading = [
        ...headers.map((id) => ({ displayName: `${id}`, key: `${id}` })),
        { displayName: "Remark", key: "remark" },
        { displayName: "Status", key: "status", textAlign: true },
      ];
      setPreviewColumn(heading);

      const validatedData = validateData(jsonData) || [];

      const vailidDataFilter = validatedData.filter(
        (item) => item?.validFlag == 1,
      );
      const invailidDataFilter = validatedData.filter(
        (item) => item?.validFlag == 0 || item?.validFlag == 2,
      );
      setTotalUpload(jsonData?.length);
      setVailidData(vailidDataFilter?.length);

      if (invailidDataFilter && invailidDataFilter.length > 0) {
        setIsvailidFlag(true);
        setPreviewData(invailidDataFilter);
      } else {
        setPreviewData(validatedData);
      }
    };
  };

  const rowData = useMemo(() => {
    if (previewData && previewData.length > 0) {
      return previewData.map((item) => ({
        ...item,
        status:
          item.validFlag === 1 ? (
            <div style={{ textAlign: "center" }}>
                <span className="text-success fw-semibold">Valid</span>
              {/* <i className="ri-checkbox-circle-fill text-success fs-5"></i> */}
            </div>
          ) : (
            <div style={{ textAlign: "center" }}>
               <span className="text-danger fw-semibold">Invalid</span>
              {/* <i className="ri-close-circle-fill text-danger fs-5"></i> */}
            </div>
          ),
      }));
    }
    return [];
  }, [previewData]);

  const validateData = (rows) => {
    const serialSeen = new Set();
    const lpaSeen = new Set();

    return rows.map((row) => {
      let remark = [];
      let validFlag = 1;

      const serial = String(row.SerialNumber || "").trim();
      const lpa = String(row.LPA || "").trim();

      // SerialNumber checks
      if (!serial) {
        remark.push("Serial number is missing");
        validFlag = 0;
      } else if (!/^\d+$/.test(serial)) {
        remark.push("Serial number must be numeric");
        validFlag = 0;
      } else if (serial.length !== 19) {
        remark.push("Serial number must be 19 digits");
        validFlag = 0;
      } else if (serialSeen.has(serial)) {
        remark.push("Duplicate serial number");
        validFlag = 2;
      } else {
        serialSeen.add(serial);
      }

      //  LPA checks
      if (!lpa) {
        remark.push("LPA is missing");
        validFlag = 0;
      } else if (lpaSeen.has(lpa)) {
        remark.push("Duplicate LPA");
        validFlag = 2;
      } else {
        lpaSeen.add(lpa);
      }

      return {
        ...row,
        SerialNumber: serial,
        LPA: lpa,
        validFlag,
        remark: remark.length ? remark.join(", ") : "Valid",
      };
    });
  };

  const fResultColumn = [
    { displayName: "Serial Number", key: "serialNumber" },
    { displayName: "Remark", key: "remark" },
    { displayName: "Status", key: "status", textAlign: true },
  ]

  const fRowData = useMemo(() => {
    return Array.isArray(purchaseInventoryFResult) && purchaseInventoryFResult.length > 0
      ? purchaseInventoryFResult.map((item, key) => {
        return {
          key: key + 1,
          serialNumber: item?.simNo,
          status: item?.responseCode == 0 ? (
            <div style={{ textAlign: "center" }}>
              <i className="ri-checkbox-circle-fill text-success fs-5"></i>
            </div>
          ) : (
            <div style={{ textAlign: "center" }}>
              <i className="ri-close-circle-fill text-danger fs-5"></i>
            </div>
          ),
          remark: item?.responseMsg,
        };
      })
      : [];
  }, [purchaseInventoryFResult]);

  const downloadResponseFile = () => {
    const data = purchaseInventoryFResult && purchaseInventoryFResult.length > 0 && purchaseInventoryFResult.map((item, ind) => (
      {
        "Serial Number": item?.simNo,
        "Remark": item?.responseMsg,
        "Status": item?.responseCode === 0 ? "Valid" : "Invalid"
      }));

    downloadExcelSampleFile(data, "purchase-inventory-response-file.xlsx");
  }


  const handleBack = () => {
    dispatch(resetMessage())
  }

  function StatCard({ icon, iconBg, label, value, sub }) {
    return (
      <div className="col ">
        <div className="border rounded-4 py-2 px-3 h-100 d-flex gap-3 align-items-start">
          <div
            className="d-flex align-items-center justify-content-center rounded-circle text-white flex-shrink-0"
            style={{ width: 36, height: 36, background: iconBg }}
          >
            <i className={icon} />
          </div>
          <div>
            <div className="text-primary small fw-semibold">{label}</div>
            <div className="fs-4 fw-bold lh-1">{value}</div>
            <div className="text-muted small">{sub}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CardLayout
      title="Purchase SIM"
      icon="ri-file-chart-line"
      rightContent={
        <div>
          {purchaseInventoryFResult && purchaseInventoryFResult.length > 0 ?
            <div className="d-flex gap-2">
              <Button
                title="Back"
                buttonType="button"
                icon={<i className="ri-arrow-left-line me-1" />}
                variant="primary"
                style={{ padding: "5px 10px", }}
                onClick={() => handleBack()}
              />
              <Button
                title="Excel"
                buttonType="button"
                icon={<i className="ri-download-cloud-line me-1" />}
                variant="secondary"
                style={{ padding: "5px 10px", }}
                onClick={downloadResponseFile}
              />
            </div>
            :
            <Button
              title=" Sample File"
              buttonType="button"
              icon={<i className="ri-download-cloud-line me-1" />}
              variant="secondary"
              style={{
                padding: "5px 10px",
              }}
              onClick={downloadSampleFile}
            />
          }
        </div>
      }
    >
      {purchaseInventoryFResult && purchaseInventoryFResult.length > 0 ?
        <div>
          <div className="mt-1">
            <Table
              columns={fResultColumn}
              loading={false}
              data={fRowData}
              pagination={true}
            />
          </div>
        </div> :
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="row">
          {/* Purchase Number (read-only) */}
          <div className="mb-3 col-lg-4 col-md-6">
            <label className="fw-semibold mb-2">
              Purchase Number{" "}
              <span style={{ fontSize: "11px", fontWeight: "400" }}>
                (Auto Generated)
              </span>
            </label>

            <div className="input-group">
              <input
                type="text"
                className="form-control bg-light"
                readOnly
                disabled
                {...register("purchaseNo", {
                  required: "Purchase Number is required",
                })}
              />
              <span
                className="input-group-text bg-white"
                style={{ cursor: "pointer" }}
                onClick={() => handleCopy(watchPurchaseNumber)}
              >
                <i
                  className={`${copiedField ? "ri-check-line" : "ri-file-copy-line"}`}
                ></i>
              </span>
            </div>
          </div>

          {/* Purchase Date */}
          <div className="col-lg-4 col-md-6 mb-3">
            <label className="form-label fw-semibold">
              Purchase Date{" "}
              <span style={{ fontSize: "11px", fontWeight: "400" }}>
                (MM-DD-YYYY)
              </span>
            </label>
            <input
              type="date"
              disabled
              className={`form-control purchase-date ${errors.purchaseDate ? "is-invalid" : ""}`}
              {...register("purchaseDate", { required: true })}
            />
            {errors.purchaseDate && (
              <div className="invalid-feedback">Purchase date is required.</div>
            )}
          </div>

          {/* Expiry Date */}
          <div className="col-lg-4 col-md-6 mb-3">
            <label className="form-label fw-semibold">
              Expiry Date{" "}
              <span style={{ fontSize: "11px", fontWeight: "400" }}>
                (MM-DD-YYYY)
              </span>{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              className={`form-control ${errors.expiryDate ? "is-invalid" : ""}`}
              {...register("expiryDate", {
                required: "Expiry date is required.",
              })}
            />
            {errors.expiryDate && (
              <div className="invalid-feedback">
                {errors?.expiryDate?.message}
              </div>
            )}
          </div>

          {/* Upload File*/}
          <div className="">
            <label className="form-label fw-semibold">
              Serial Number &amp; LPA Upload{" "}
              <span className="text-danger">*</span>
            </label>

            <div
              className={`border border-2 border-dashed rounded-4 p-2 text-center bg-light}`}
              style={{ backgroundColor: "#f8f9fa" }}
            >
              <div className="row">
                <div className="col-md-4 d-flex p-3 pb-1 gap-2 ">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-3 text-white"
                    style={{ width: 30, height: 30, background: "#1e9e5a" }}
                  >
                    <i className="ri-file-excel-2-line fs-5" />
                  </div>
                  <div className="d-flex gap-3 w-100 justify-content-between">
                    <div className="fw-semibold text-start">
                      Upload Format{" "}
                      <span style={{ fontSize: "11px", fontWeight: "400" }}>
                        (Download Sample File)
                      </span>
                    </div>
                  </div>
                  </div>
                    <div className="col-md-4 d-flex flex-column ">
                       <div className="flex-grow-1 d-flex align-items-end">
                    <label className="btn w-100 btn-outline-primary fw-semibold h-100  justify-content-center d-flex align-items-center "   style={{
                    minHeight: vailidError ? "40px" : "50px",
                    transition: "all .2s ease",
                  }}>
                      <i className="ri-upload-2-line me-2 " />
                      Upload Excel File
                      <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        hidden
                        onChange={onFileUpload}
                      />
                    </label>
                   </div>

                        {vailidError && (
                          <div
                            className=""
                            style={{
                              color: "#dc3545",
                              fontSize: "12px",
                              marginTop: "2px",
                            }}
                          >
                            {vailidError}
                          </div>
                        )}
                        </div>
                  <div className="col-md-4">
                    <div className="notes">
                      <h6>Note :</h6>
                      <ul
                        className=" text-start ps-3"
                        style={{ listStyle: "disc" }}
                      >
                        <li style={{ listStyle: "outside" }}>
                          Excel must contain two columns: Serial Number and LPA
                        </li>
                        <li style={{ listStyle: "outside" }}>
                          Accepted format: .xlsx, .xls
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
             
            </div>

          {rowData && rowData.length > 0 && (
            <div className="mt-1">
              <Table
                columns={previewColumn}
                loading={false}
                data={rowData}
                pagination={true}
                tableHeight="200px"
              />
            </div>
          )}

          {/* Stat cards */}
          <div className="row gy-3 ">
            <StatCard
              icon="ri-list-check-2"
              iconBg="#1341c9"
              label="Total Records"
              value={totalUpload}
              sub={`${MAX_RECORDS.toLocaleString()} (Max)`}
            />
            <StatCard
              icon="ri-checkbox-circle-fill"
              iconBg="#1e9e5a"
              label="Valid Records"
              value={vailidData}
              sub="Ready to purchase"
            />
            <StatCard
              icon="ri-alert-fill"
              iconBg="#e08a1e"
              label="Invalid Records"
              value={
                (rowData &&
                  rowData.length > 0 &&
                  rowData?.filter((item) => item.validFlag == 0)?.length) ||
                0
              }
              sub="Will be excluded"
            />
            <StatCard
              icon="ri-close-circle-fill"
              iconBg="#d43f3f"
              label="Duplicate Records"
              value={
                (rowData &&
                  rowData.length > 0 &&
                  rowData?.filter((item) => item.validFlag == 2)?.length) ||
                0
              }
              sub="Will be excluded"
            />
          </div>
          <hr className="mt-1"/>

          {/* Actions */}
          <div className="d-flex justify-content-end gap-3">
            <div className="">
              <Button
                title="Reset"
                buttonType="button"
                icon={<i className="ri-refresh-line me-2" />}
                variant="secondary"
                onClick={handleReset}
                style={{ padding: "5px 20px", width: "100%" }}
              />
            </div>
            <div className="">
              <Button
                title="Purchase SIM"
                buttonType="submit"
                icon={<i className="ri-shopping-cart-line me-2" />}
                variant="primary"
                loading={purchaseInventoryLoading}
                style={{
                  padding: purchaseInventoryLoading ? "9px 20px" : "5px 20px",
                  minWidth: "170px",
                }}
                disable={isvailidFlag || purchaseInventoryLoading || (!rowData || rowData?.length == 0)}
              />
            </div>
          </div>
        </form>}
    </CardLayout>
  );
};

export default PurchaseSim;
